import { animated, useTransition } from "@react-spring/web";
import { Group } from "@visx/group";
import React, { useMemo } from "react";
import { DataPoint } from "../../types";
import { BarChartProps } from "./types";

const BarChart = ({
  top,
  left,
  yMax,
  xMax,
  data,
  getLeftPoint,
  getRightPoint,
  leftScale,
  rightScale,
  coloursScale,
  getBottomPoint,
  bottomScale,
  onMouseLeave,
  onPointerMove,
  colour,
}: BarChartProps) => {
  const shouldIncludeZero = useMemo(
    () =>
      data.some((set) => {
        const { axis, data: setData } = set;

        const getSidePoint = axis === "left" ? getLeftPoint : getRightPoint;
        const zeroPosition =
          yMax - (axis === "left" ? leftScale(0) : rightScale(0));

        return setData.some((point: DataPoint) => {
          const y = getSidePoint?.(point);

          return y < zeroPosition;
        });
      }),
    [data]
  );

  const bars = useMemo(() => {
    const unsorted = data.reduce((total: any, set) => {
      const { axis, label, data: setData } = set;

      const getSidePoint = axis === "left" ? getLeftPoint : getRightPoint;
      const zeroPosition = axis === "left" ? leftScale(0) : rightScale(0);

      const displayData = setData.map((point: DataPoint) => {
        const y = getSidePoint?.(point);

        let height = 0;

        if (shouldIncludeZero) {
          if (y < zeroPosition) {
            height = yMax - (yMax - zeroPosition) - y;
          } else {
            height = yMax - zeroPosition - (yMax - y);
          }
        } else {
          height = yMax - y;
        }

        let sidePosition = y;

        if (y > zeroPosition) {
          sidePosition = zeroPosition;
        }

        return {
          ...point,
          height,
          key: `${label}-${y?.toString()}-${getBottomPoint(point)}`,
          fill: colour ?? coloursScale(label),
          y: sidePosition,
          axis,
          negative: y > zeroPosition,
          zeroPosition,
        };
      });

      return [...total, ...displayData];
    }, [] as any[]);

    return unsorted.sort(({ y: aY }: DataPoint, { y: bY }: DataPoint) =>
      aY < bY ? -1 : 1
    );
  }, [data, yMax]);

  const transitions = useTransition(bars, {
    keys: ({ key }) => key,
    from: ({ negative, y, zeroPosition }) => ({
      height: 0,
      y: !shouldIncludeZero ? yMax : negative ? y : zeroPosition,
    }),
    enter: ({ height, y }) => ({
      height,
      y,
    }),
    trail: 15,
  });

  return (
    <Group
      top={top}
      left={left}
      height={yMax}
      width={xMax}
    >
      {transitions(({ height, y }: DataPoint, point, _, index) => (
        <animated.rect
          key={`bar-${index}`}
          x={getBottomPoint?.(point)}
          y={y}
          height={height}
          width={bottomScale.bandwidth()}
          fill={point.fill}
          rx={4}
          onMouseLeave={onMouseLeave}
          onPointerMove={(event) => {
            if (onPointerMove) {
              onPointerMove(event, point);
            }
          }}
        />
      ))}
    </Group>
  );
};

export default BarChart;
